import { useEffect, useRef, useState } from "react"
import { Flag, X } from "lucide-react"

/**
 * Modal zum Markieren einer E-Mail mit roter Fahne + optionaler Notiz.
 * Wird in ReviewPage und EmailsList genutzt.
 *
 * Notiz ist optional — Enter / Klick auf "Markieren" speichert auch ohne.
 * Esc oder Backdrop-Klick schliesst ohne zu speichern.
 */
export function FlagNoteModal({
  open,
  subject,
  initialNote = "",
  onSave,
  onClose,
}: {
  open: boolean
  subject: string
  initialNote?: string
  onSave: (note: string) => void
  onClose: () => void
}) {
  const [note, setNote] = useState(initialNote)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (open) {
      setNote(initialNote)
      // Autofocus nach Render
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [open, initialNote])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open, onClose])

  if (!open) return null

  const handleSubmit = () => {
    onSave(note.trim())
  }

  return (
    <div
      className="pp-overlay"
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.4)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{
        background: "#fff", borderRadius: 12, width: "100%", maxWidth: 460,
        boxShadow: "0 20px 60px rgba(0,0,0,.25)",
      }}>
        {/* Header */}
        <div style={{
          padding: "14px 20px", borderBottom: "0.5px solid rgba(44,44,42,0.1)",
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <Flag size={16} fill="#dc2626" color="#dc2626" strokeWidth={0} />
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Mail markieren</h3>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            onClick={onClose}
            aria-label="Schliessen"
            style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{
            fontSize: 12, color: "#5F5E5A", padding: "8px 10px",
            background: "rgba(44,44,42,0.04)", borderRadius: 6,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {subject || "(ohne Betreff)"}
          </div>
          <div>
            <label
              htmlFor="flag-note-input"
              style={{
                display: "block", fontSize: 12, fontWeight: 500,
                color: "#5F5E5A", marginBottom: 6,
                textTransform: "uppercase", letterSpacing: 0.5,
              }}
            >
              Notiz (optional)
            </label>
            <textarea
              id="flag-note-input"
              ref={inputRef}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault()
                  handleSubmit()
                }
              }}
              placeholder="z.B. 'Müller anrufen vor Mittwoch'"
              maxLength={500}
              rows={3}
              style={{
                width: "100%", fontSize: 13, padding: "8px 10px",
                border: "1px solid rgba(44,44,42,0.15)", borderRadius: 6,
                resize: "vertical", fontFamily: "inherit", color: "#2C2C2A",
                background: "#fff", outline: "none",
                lineHeight: 1.5,
              }}
            />
            <div style={{
              fontSize: 12, color: "#888780", marginTop: 4,
              display: "flex", justifyContent: "space-between",
            }}>
              <span>Cmd/Ctrl+Enter zum Markieren · Esc zum Abbrechen</span>
              <span>{note.length}/500</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: "12px 20px", borderTop: "0.5px solid rgba(44,44,42,0.1)",
          display: "flex", gap: 8, justifyContent: "flex-end",
        }}>
          <button
            type="button"
            onClick={onClose}
            className="pp-btn pp-btn-sm"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            style={{
              background: "#dc2626", color: "#fff", border: "none",
              padding: "8px 16px", borderRadius: 6, fontSize: 13, fontWeight: 500,
              cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            <Flag size={13} fill="#fff" color="#fff" strokeWidth={0} />
            Markieren
          </button>
        </div>
      </div>
    </div>
  )
}
